import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CardElement, Elements, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { FaApple, FaGooglePlay } from "react-icons/fa";
import WebsiteLayout from "../layouts/WebsiteLayout";
import { vibesColors as colors } from "../theme/vibesColors";
import {
  postBoothBottleReviewSummary,
  postBottleReviewSummary,
  postReviewSummary,
  postWebBooking,
} from "../services/apiService";
import {
  buildBottleCatalog,
  buildReviewBottlePayload,
  formatBottleCurrency,
  getDefaultReservationPrice,
  pickBottleDayPricing,
  pickBottleName,
  readBottleServiceId,
} from "../utils/bottleServiceHelpers.js";
import { isApiSuccess } from "../utils/clubDetailHelpers";
import { getPriceForDay, pickMaleFemalePrices } from "../utils/eventDetailHelpers";
import BackNavButton from "../components/common/BackNavButton";
import "./ReviewSummaryPage.css";
import ReviewEventCard from "../components/review-summary/ReviewEventCard";
import PricingSummaryCard from "../components/review-summary/PricingSummaryCard";
import GuestUserForm from "../components/review-summary/GuestUserForm";

const STRIPE_PUBLISHABLE_KEY =
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "pk_test_51SBVvYG1YDeYbMZgrHDZOLANzAQPUnvdLk46zn6rz2JPrwAKdi3cMD4q5ccvacsFgq5dlvRKS04gvXhjK0cYwF9S004UuIYPr1";
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
const APP_STORE_URL =
  process.env.REACT_APP_APP_STORE_URL || "https://apps.apple.com/";
const PLAY_STORE_URL =
  process.env.REACT_APP_PLAY_STORE_URL || "https://play.google.com/store";

function money(v) {
  const n = Number(v || 0);
  return `$${n.toFixed(2)}`;
}

function formatDateLabel(value) {
  if (!value) return "";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return String(value);
  return dt.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function toDmyShort(ymd) {
  const [y, m, d] = String(ymd || "").split("-");
  if (!y || !m || !d) return "";
  return `${d}-${m}-${String(y).slice(-2)}`;
}

function eachBookingDateYmd(startYmd, endYmd) {
  if (!startYmd) return [];
  const list = [];
  const start = new Date(`${startYmd}T12:00:00`);
  const end = new Date(`${endYmd || startYmd}T12:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [startYmd];
  const cursor = new Date(start);
  while (cursor <= end) {
    const y = cursor.getFullYear();
    const m = String(cursor.getMonth() + 1).padStart(2, "0");
    const d = String(cursor.getDate()).padStart(2, "0");
    list.push(`${y}-${m}-${d}`);
    cursor.setDate(cursor.getDate() + 1);
  }
  return list.length ? list : [startYmd];
}

function pickSummaryTicketOrBoothCost(...sources) {
  const keys = [
    "ticketCost",
    "ticketcost",
    "TicketCost",
    "boothCost",
    "boothcost",
    "BoothCost",
    "boothPrice",
    "ticketPrice",
    "boothTotal",
  ];
  for (const source of sources) {
    if (!source || typeof source !== "object") continue;
    for (const key of keys) {
      const raw = source[key];
      if (raw == null || raw === "") continue;
      const num = Number(raw);
      if (!Number.isNaN(num) && num > 0) return num;
    }
  }
  return 0;
}

function boothOrTicketCostFromSelection(selectedItem, members, bookingDays, startYmd, endYmd) {
  if (!selectedItem || typeof selectedItem !== "object") return 0;
  const priceObj =
    selectedItem?.discountedPrice ??
    selectedItem?.ticketPrice ??
    selectedItem?.boothPrice ??
    selectedItem?.price;
  if (priceObj == null) return 0;

  const dates = eachBookingDateYmd(startYmd, endYmd);
  let perMemberTotal = 0;
  for (const ymd of dates) {
    const dt = new Date(`${ymd}T12:00:00`);
    if (Number.isNaN(dt.getTime())) continue;
    const dayKey = dt.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
    const dayShort = dt.toLocaleDateString("en-US", { weekday: "short" });
    const dayPrice = getPriceForDay(priceObj, dayKey, dayShort);
    if (dayPrice != null) perMemberTotal += dayPrice;
  }

  if (perMemberTotal <= 0) {
    if (typeof priceObj === "number") perMemberTotal = priceObj;
    else if (typeof priceObj === "string" && !Number.isNaN(Number(priceObj))) {
      perMemberTotal = Number(priceObj);
    }
  }

  if (perMemberTotal <= 0) return 0;
  const days = Math.max(1, Number(bookingDays || dates.length || 1));
  const memberCount = Math.max(1, Number(members || 1));
  if (dates.length > 1) {
    return perMemberTotal * memberCount;
  }
  return perMemberTotal * memberCount * days;
}

function getCurrentDayPrice(event) {
  if (!event || typeof event !== "object") return null;
  const dayDate = new Date();
  const dayKey = dayDate.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
  const dayShort = dayDate.toLocaleDateString("en-US", { weekday: "short" });
  const readDayValue = (obj) => {
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) return null;
    const cap = dayKey.charAt(0).toUpperCase() + dayKey.slice(1);
    const candidates = [dayKey, cap, dayKey.toUpperCase(), dayShort, dayShort.toLowerCase(), dayShort.toUpperCase()];
    for (const key of candidates) {
      const raw = obj?.[key];
      if (raw == null || raw === "") continue;
      const num = Number(raw);
      if (!Number.isNaN(num)) return num;
    }
    return null;
  };

  const candidates = [
    event?.entryFee,
    event?.ticketPrice,
    event?.malePrice,
    event?.femalePrice,
    event?.entry_fee,
    event?.priceByDay,
  ];
  for (const source of candidates) {
    if (!source) continue;
    if (typeof source === "number") return source > 0 ? source : null;
    if (typeof source === "string" && source.trim() !== "" && !Number.isNaN(Number(source))) {
      const n = Number(source);
      return n > 0 ? n : null;
    }
    const dayValue = readDayValue(source);
    if (dayValue != null) return dayValue > 0 ? dayValue : null;
  }
  return null;
}

function pickBoothTypeId(selectedBooth) {
  const raw = selectedBooth?.boothType;
  if (!raw) return "";
  if (typeof raw === "string") return raw.trim();
  if (typeof raw === "object") return String(raw._id || raw.id || "").trim();
  return "";
}

function computeTableGenderPricing(event, bookingInput) {
  const maleCount = Number(bookingInput?.maleGuests || 0);
  const femaleCount = Number(bookingInput?.femaleGuests || 0);
  const dates = eachBookingDateYmd(
    bookingInput?.selectedDate,
    bookingInput?.selectedEndDate || bookingInput?.selectedDate
  );
  const { malePrice: malePriceObj, femalePrice: femalePriceObj } = pickMaleFemalePrices(event);

  let maleTotal = 0;
  let femaleTotal = 0;
  for (const ymd of dates) {
    const dt = new Date(`${ymd}T12:00:00`);
    if (Number.isNaN(dt.getTime())) continue;
    const dayKey = dt.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
    const dayShort = dt.toLocaleDateString("en-US", { weekday: "short" });
    maleTotal += Number(getPriceForDay(malePriceObj, dayKey, dayShort) || 0) * maleCount;
    femaleTotal += Number(getPriceForDay(femalePriceObj, dayKey, dayShort) || 0) * femaleCount;
  }

  const combinedFromInput = Number(bookingInput?.maleFemalePrice || 0);
  if (maleTotal + femaleTotal <= 0 && combinedFromInput > 0) {
    if (maleCount > 0 && femaleCount === 0) maleTotal = combinedFromInput;
    else if (femaleCount > 0 && maleCount === 0) femaleTotal = combinedFromInput;
  }

  return {
    maleCount,
    femaleCount,
    maleTotal,
    femaleTotal,
    combinedTotal: maleTotal + femaleTotal,
  };
}

function getBottleReservationPrice(...sources) {
  for (const source of sources) {
    if (!source || typeof source !== "object") continue;
    const raw =
      source?.bottleService ||
      source?.bottleServices ||
      source?.bottleMenu ||
      source?.menu ||
      source?.bottles ||
      null;
    if (!raw || typeof raw !== "object" || Array.isArray(raw)) continue;
    const v = raw?.reservationPrice;
    if (v !== undefined && v !== null && !Number.isNaN(Number(v))) return Number(v);
  }
  return null;
}

function formatUsPhoneMask(input) {
  const digits = String(input || "").replace(/\D/g, "");
  const core = digits.startsWith("1") ? digits.slice(1, 11) : digits.slice(0, 10);
  const a = core.slice(0, 3);
  const b = core.slice(3, 6);
  const c = core.slice(6, 10);
  if (!a) return "+1 ";
  if (!b) return `+1 (${a}`;
  if (!c) return `+1 (${a}) ${b}`;
  return `+1 (${a}) ${b}-${c}`;
}

function normalizeUsPhoneForApi(input) {
  const digits = String(input || "").replace(/\D/g, "");
  const core = digits.startsWith("1") ? digits.slice(1, 11) : digits.slice(0, 10);
  return core.length === 10 ? `+1${core}` : "";
}

function guestFormStorageKey(eventId) {
  return eventId ? `vibeReviewGuest:${eventId}` : "";
}

function readGuestFormDraft(eventId) {
  const key = guestFormStorageKey(eventId);
  if (!key) return {};
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function ReviewSummaryPageContent() {
  const { eventId = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromEventUrl = searchParams.get("fromEvent") === "1";
  const state = location.state || {};
  const [summary, setSummary] = useState(state.reviewSummary || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [promoCodeInput, setPromoCodeInput] = useState("");
  const [appliedPromoCode, setAppliedPromoCode] = useState("");
  const [promoError, setPromoError] = useState("");
  const guestDraft = useMemo(() => readGuestFormDraft(eventId), [eventId]);
  const [userName, setUserName] = useState(() => String(guestDraft.userName || ""));
  const [userEmail, setUserEmail] = useState(() => String(guestDraft.userEmail || ""));
  const [userDob, setUserDob] = useState(() => String(guestDraft.userDob || ""));
  const [contactNumber, setContactNumber] = useState(() => String(guestDraft.contactNumber || ""));
  const [nameError, setNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [dobError, setDobError] = useState("");
  const [contactError, setContactError] = useState("");
  const dobInputRef = useRef(null);
  const guestDobInitializedRef = useRef(Boolean(guestDraft.userDob));
  const appliedPromoRef = useRef(appliedPromoCode);
  appliedPromoRef.current = appliedPromoCode;
  const [cardName, setCardName] = useState(() => String(guestDraft.cardName || ""));
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState("");
  const [paymentError, setPaymentError] = useState("");
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const data = summary?.data || {};
  const eventFromSummary = data?.data && typeof data.data === "object" ? data.data : data;
  const event = { ...(state?.event || {}), ...(eventFromSummary || {}) };
  const apiSummary =
    data?.summary ||
    eventFromSummary?.summary ||
    summary?.summary ||
    {};
  const bookingInput = state?.bookingInput || {};
  console.log("ReviewSummaryPage data:", { summary, bookingInput, event });
  const hostId =
    bookingInput?.hostId ||
    event?.hostId?._id ||
    event?.hostId ||
    event?.userId?._id ||
    event?.userId ;
  const eventImage =
    event?.photos?.[0] ||
    event?.image ||
    event?.bannerImage ||
    event?.businessBanner ||
    "";
  const startDateLabel = formatDateLabel(
    bookingInput?.selectedDate || event?.startDate || event?.eventStartDate
  );
  const startTimeLabel = event?.openingTime || event?.startTime || "";
  const currentDayPrice = getCurrentDayPrice(event);
  const bottleReservationPrice = getBottleReservationPrice(event, eventFromSummary, data, state?.event);
  const isTableBooking =
    String(bookingInput?.isTable || "").toLowerCase() === "yes" ||
    String(event?.type || "").toLowerCase() === "table";
  const isBottleServiceBooking =
    String(bookingInput?.type || "").toLowerCase() === "bottleservice" ||
    String(event?.type || "").toLowerCase() === "bottleservice" ||
    String(event?.type || "").toLowerCase() === "bottle service";
  const isBoothTypeBooking = String(bookingInput?.type || event?.type || "").trim().toLowerCase() === "booth";
  const hasBoothBottleSelection =
    (Array.isArray(bookingInput?.alcoholic) && bookingInput.alcoholic.length > 0) ||
    (Array.isArray(bookingInput?.nonAlcoholic) && bookingInput.nonAlcoholic.length > 0);
  const hasBoothBottleService = Boolean(bookingInput?.hasBoothBottleService && hasBoothBottleSelection);
  const bookingType = String(bookingInput?.type || "").trim().toLowerCase();
  const bookingIsTable = String(bookingInput?.isTable || "").toLowerCase() === "yes";
  const bookingIsBottleService =
    bookingType === "bottleservice" || bookingType === "bottle service";
  const bookingIsClub = bookingType === "club";
  const usesBottlePricing = isBottleServiceBooking || hasBoothBottleService;
  const isClubBooking =
    String(bookingInput?.type || "").toLowerCase() === "club" ||
    String(event?.type || "").toLowerCase() === "club";
  const membersCount = Number(bookingInput?.members || 1);
  const bookingDays = Number(bookingInput?.days || 1);
  const hasTicketBooking = Boolean(String(bookingInput?.ticketId || "").trim());
  const hasBoothBooking =
    isClubBooking && Boolean(String(bookingInput?.boothId || "").trim());
  const usesTicketPricing =
    !isTableBooking && (hasTicketBooking || hasBoothBooking);
  const bottleDetails = useMemo(
    () => [
      ...(Array.isArray(bookingInput?.alcoholic) ? bookingInput.alcoholic.map((x) => ({ ...x, bottleType: "Alcoholic" })) : []),
      ...(Array.isArray(bookingInput?.nonAlcoholic)
        ? bookingInput.nonAlcoholic.map((x) => ({ ...x, bottleType: "Non-Alcoholic" }))
        : []),
    ],
    [bookingInput?.alcoholic, bookingInput?.nonAlcoholic]
  );
  const bottleCatalogItemsById = useMemo(() => {
    const map = new Map();
    [state?.event, event].filter(Boolean).forEach((source) => {
      const catalog = buildBottleCatalog(source);
      [...(catalog.alcoholic || []), ...(catalog.nonAlcoholic || [])].forEach((item) => {
        const id = readBottleServiceId(item);
        if (id && !map.has(id)) map.set(id, item);
      });
    });
    return map;
  }, [event, state?.event]);
  const defaultBottleReservationPrice = useMemo(
    () => getDefaultReservationPrice(state?.event) || getDefaultReservationPrice(event),
    [event, state?.event]
  );

  const tableGenderPricing = isTableBooking
    ? computeTableGenderPricing(event, bookingInput)
    : null;
  let entryFeeAmount = Number(apiSummary?.memberCost ?? data?.memberCost ?? 0);
  if (isTableBooking && entryFeeAmount <= 0) {
    entryFeeAmount = Number(
      apiSummary?.maleFemalePrice ??
        data?.maleFemalePrice ??
        bookingInput?.maleFemalePrice ??
        tableGenderPricing?.combinedTotal ??
        0
    );
  }
  const perDayFromSummary = (() => {
    const denom = Math.max(1, Number(membersCount || 1) * Number(bookingDays || 1));
    const v = Number(entryFeeAmount || 0) / denom;
    return v > 0 ? v : null;
  })();
  let ticketPrice = isTableBooking
    ? 0
    : pickSummaryTicketOrBoothCost(
        apiSummary,
        data,
        summary,
        data?.summary,
        eventFromSummary?.summary
      );

  if (!isTableBooking && hasBoothBooking && ticketPrice <= 0) {
    ticketPrice = boothOrTicketCostFromSelection(
      bookingInput?.selectedBooth,
      membersCount,
      bookingDays,
      bookingInput?.selectedDate,
      bookingInput?.selectedEndDate
    );
  }

  if (!isTableBooking && hasTicketBooking && ticketPrice <= 0) {
    ticketPrice = boothOrTicketCostFromSelection(
      bookingInput?.selectedTicket,
      membersCount,
      bookingDays,
      bookingInput?.selectedDate,
      bookingInput?.selectedEndDate
    );
  }

  const perTicketDayFromSummary = (() => {
    const denom = Math.max(1, Number(membersCount || 1) * Number(bookingDays || 1));
    const v = Number(ticketPrice || 0) / denom;
    return v > 0 ? v : null;
  })();
  const bottleServiceTotal = Number(
    apiSummary?.bottleServiceTotal ??
      apiSummary?.bottleServiceCost ??
      data?.bottleServiceTotal ??
      data?.bottleServiceCost ??
      0
  );
  let boothCost = 0;
  if (hasBoothBottleService) {
    boothCost = Number(apiSummary?.boothCost ?? data?.boothCost ?? apiSummary?.memberCost ?? data?.memberCost ?? 0);
    if (boothCost <= 0) {
      boothCost = boothOrTicketCostFromSelection(
        event,
        membersCount,
        bookingDays,
        bookingInput?.selectedDate,
        bookingInput?.selectedEndDate || bookingInput?.selectedDate
      );
    }
    if (boothCost <= 0) {
      boothCost = Number(entryFeeAmount || 0);
    }
  }
  const alcoholicTotal = Number(
    apiSummary?.alcoholicBottleServiceTotalPrice ??
      apiSummary?.alcoholicTotal ??
      data?.alcoholicBottleServiceTotalPrice ??
      data?.alcoholicTotal ??
      0
  );
  const nonAlcoholicTotal = Number(
    apiSummary?.nonAlcoholicTotalPrice ??
      apiSummary?.nonAlcoholicTotal ??
      data?.nonAlcoholicTotalPrice ??
      data?.nonAlcoholicTotal ??
      0
  );
  const getBottleLinePrice = (item) => {
    if (item?.bottlePrice != null || item?.price != null) {
      return formatBottleCurrency(Number(item.bottlePrice ?? item.price ?? 0));
    }

    const id = String(item?.bottleServiceId || item?.BottleServiceId || "").trim();
    let catalogItem = bottleCatalogItemsById.get(id);
    if (!catalogItem && item?.name) {
      for (const [, entry] of bottleCatalogItemsById) {
        if (pickBottleName(entry) === item.name) {
          catalogItem = entry;
          break;
        }
      }
    }
    if (catalogItem) {
      const { bottlePrice } = pickBottleDayPricing(
        catalogItem,
        bookingInput?.selectedDate || "",
        defaultBottleReservationPrice
      );
      return formatBottleCurrency(bottlePrice);
    }

    return "";
  };
  const reservationFeeAmount = Number(
    apiSummary?.reservationFee ??
      apiSummary?.reservation_fee ??
      data?.reservationFee ??
      data?.reservation_fee ??
      bottleReservationPrice ??
      0
  );
  const bottleSubtotalFallback =
    bottleServiceTotal > 0
      ? bottleServiceTotal
      : alcoholicTotal + nonAlcoholicTotal;
  const bottlePricingBase = hasBoothBottleService
    ? boothCost + bottleSubtotalFallback
    : bottleSubtotalFallback;
  const discount = Number(apiSummary?.discount ?? data?.discount ?? 0);
  const hst = Number(apiSummary?.fees ?? 0);
  const platformFee = Number(apiSummary?.adminFee ?? 0);
  const gratuityPercent = Number(
    data?.gratuityValue ??
      apiSummary?.gratuityValue ??
      data?.gratuityPer ??
      apiSummary?.gratuityPer ??
      0
  );
  let gratuityAmount = Number(
    apiSummary?.gratuityAmount ??
      data?.gratuityAmount ??
      summary?.gratuityAmount ??
      data?.gratuity ??
      apiSummary?.gratuity ??
      0
  );
  if (gratuityAmount <= 0) {
    const isGratuityEnabled =
      event?.isGratuity === true ||
      data?.isGratuity === true ||
      apiSummary?.isGratuity === true;
    const gratuityRate = Number(
      event?.gratuityValue ??
        data?.gratuityValue ??
        apiSummary?.gratuityValue ??
        gratuityPercent ??
        0
    );
    if (isGratuityEnabled && gratuityRate > 0) {
      const baseAmount =
        usesTicketPricing
          ? ticketPrice
          : usesBottlePricing
            ? bottlePricingBase
            : entryFeeAmount;
      gratuityAmount = (Number(baseAmount || 0) * gratuityRate) / 100;
    }
  }
  const total = Number(
    apiSummary?.total ??
      data?.totalAmount ??
      data?.total ??
      apiSummary?.totalPrice ??
      data?.totalPrice ??
      (usesBottlePricing
        ? bottlePricingBase
        : usesTicketPricing
          ? ticketPrice
          : entryFeeAmount) -
        discount +
        hst +
        platformFee +
        gratuityAmount
  );
  const subtotal = usesBottlePricing ? bottlePricingBase : null;
  const pricing = {
    entryFeeAmount,
    maleCount: tableGenderPricing?.maleCount ?? 0,
    femaleCount: tableGenderPricing?.femaleCount ?? 0,
    maleTotal: tableGenderPricing?.maleTotal ?? 0,
    femaleTotal: tableGenderPricing?.femaleTotal ?? 0,
    ticketPrice,
    boothCost,
    bottleServiceTotal,
    alcoholicTotal,
    nonAlcoholicTotal,
    reservationFeeAmount,
    subtotal,
    discount,
    hst,
    platformFee,
    gratuityPercent,
    gratuityAmount,
    total,
  };
  const ticketDayPrice = hasTicketBooking
    ? getCurrentDayPrice(bookingInput?.selectedTicket || event)
    : null;
  const boothDayPrice = hasBoothBooking
    ? (() => {
        const total = boothOrTicketCostFromSelection(
          bookingInput?.selectedBooth,
          1,
          1,
          bookingInput?.selectedDate,
          bookingInput?.selectedEndDate
        );
        if (total > 0) return total;
        return getCurrentDayPrice({
          entryFee:
            bookingInput?.selectedBooth?.discountedPrice ??
            bookingInput?.selectedBooth?.boothPrice,
        });
      })()
    : null;
  const reviewCardPrice =
    isTableBooking || usesBottlePricing
      ? null
      : hasTicketBooking
        ? ticketDayPrice ?? perTicketDayFromSummary
        : hasBoothBooking
          ? boothDayPrice ?? perTicketDayFromSummary
          : currentDayPrice ?? perDayFromSummary;
  const isValidEmail = (value) => {
    const email = String(value || "").trim();
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
  };

  const minAdultDobIso = (() => {
    const now = new Date();
    const cutoff = new Date(now.getFullYear() - 18, now.getMonth(), now.getDate());
    return cutoff.toISOString().slice(0, 10);
  })();

  const isValidDob = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return false;
    const dt = new Date(raw);
    if (Number.isNaN(dt.getTime())) return false;
    return raw <= minAdultDobIso;
  };

  /** Opens native date picker; returns true when `showPicker()` ran (Chromium-style). */
  const openDobPicker = () => {
    const el = dobInputRef.current;
    if (!el) return false;
    try {
      if (typeof el.showPicker === "function") {
        el.showPicker();
        return true;
      }
    } catch (_) {
      /* e.g. not allowed — fall back to focus */
    }
    try {
      el.focus({ preventScroll: true });
    } catch (_) {
      /* ignore */
    }
    return false;
  };

  const validateGuestFields = () => {
    let ok = true;
    if (!String(userName || "").trim()) {
      setNameError("User name is required.");
      ok = false;
    } else {
      setNameError("");
    }
    if (!String(userEmail || "").trim()) {
      setEmailError("User email is required.");
      ok = false;
    } else if (!isValidEmail(userEmail)) {
      setEmailError("Please enter a valid email address.");
      ok = false;
    } else {
      setEmailError("");
    }
    if (!String(userDob || "").trim()) {
      setDobError("Date of birth is required.");
      ok = false;
    } else if (!isValidDob(userDob)) {
      setDobError("Please select a valid date of birth.");
      ok = false;
    } else {
      setDobError("");
    }
    if (!String(contactNumber || "").trim()) {
      setContactError("Contact number is required.");
      ok = false;
    } else if (!normalizeUsPhoneForApi(contactNumber)) {
      setContactError("Please enter a valid +1 contact number.");
      ok = false;
    } else {
      setContactError("");
    }
    return ok;
  };

  const validatePaymentFields = () => {
    if (!String(cardName || "").trim()) {
      setCardError("Name on card is required.");
      return false;
    }
    if (!stripe || !elements) {
      setCardError("Stripe is not ready yet. Please wait a moment.");
      return false;
    }
    if (!cardComplete) {
      setCardError("Please enter valid card details.");
      return false;
    }
    setCardError("");
    return true;
  };

  useEffect(() => {
    if (guestDobInitializedRef.current || userDob) return;
    const prefDob =
      bookingInput?.userDob ||
      bookingInput?.dateOfBirth ||
      state?.event?.userId?.dateOfBirth ||
      state?.event?.user?.dateOfBirth ||
      "";
    if (prefDob) {
      setUserDob(String(prefDob).slice(0, 10));
      guestDobInitializedRef.current = true;
    }
  }, [bookingInput?.userDob, bookingInput?.dateOfBirth, state?.event, userDob]);

  useEffect(() => {
    const key = guestFormStorageKey(eventId);
    if (!key) return;
    const payload = { userName, userEmail, userDob, contactNumber, cardName };
    const hasContent = Object.values(payload).some((value) => String(value || "").trim());
    if (!hasContent) return;
    try {
      sessionStorage.setItem(key, JSON.stringify(payload));
    } catch {
      /* ignore quota / private mode */
    }
  }, [eventId, userName, userEmail, userDob, contactNumber, cardName]);

  const fetchReviewSummary = useCallback(
    async (promoCode = "") => {
      if (!bookingInput?.eventId) return;
      setLoading(true);
      setError("");
      try {
        const startIso = `${bookingInput.selectedDate}`;
        const endIso = `${bookingInput.selectedEndDate || bookingInput.selectedDate}`;
        const res = hasBoothBottleService
          ? await postBoothBottleReviewSummary({
              eventid: bookingInput.eventId,
              bottleId: bookingInput.bottleId || "",
              members: bookingInput.members || 1,
              days: Number(bookingInput.days || 1),
              startDate: startIso,
              endDate: endIso,
              alcoholic: buildReviewBottlePayload(bookingInput.alcoholic),
              nonAlcoholic: buildReviewBottlePayload(bookingInput.nonAlcoholic),
              promocode: promoCode || "",
            })
          : bookingIsBottleService
            ? await postBottleReviewSummary({
                eventid: bookingInput.eventId,
                days: Number(bookingInput.days || 1),
                startDate: startIso,
                endDate: endIso,
                alcoholic: (bookingInput.alcoholic || []).map((item) => ({
                  BottleServiceId: item?.bottleServiceId || "",
                  image: item?.image || "",
                  name: item?.name || "",
                  quantity: Number(item?.qunatity || item?.quantity || 0),
                })),
                nonAlcoholic: (bookingInput.nonAlcoholic || []).map((item) => ({
                  BottleServiceId: item?.bottleServiceId || "",
                  image: item?.image || "",
                  name: item?.name || "",
                  quantity: Number(item?.qunatity || item?.quantity || 0),
                })),
                promocode: promoCode || "",
              })
            : await postReviewSummary({
                eventid: bookingInput.eventId,
                members: bookingInput.members || 1,
                days: Number(bookingInput.days || 1),
                startDate: toDmyShort(startIso),
                endDate: toDmyShort(endIso),
                promocode: promoCode || "",
                maleFemalePrice: Number(bookingInput.maleFemalePrice || 0),
                ...(!bookingIsTable && bookingInput.ticketId
                  ? { ticketid: bookingInput.ticketId }
                  : {}),
                ...(bookingIsClub && bookingInput.boothId
                  ? { boothId: bookingInput.boothId }
                  : {}),
              });
        if (!isApiSuccess(res?.status)) {
          setError(res?.message || "Failed to load review summary.");
          return;
        }
        setSummary(res);
      } catch (e) {
        setError(e?.message || "Failed to load review summary.");
      } finally {
        setLoading(false);
      }
    },
    [
      bookingInput?.eventId,
      bookingInput?.selectedDate,
      bookingInput?.selectedEndDate,
      bookingInput?.members,
      bookingInput?.days,
      bookingInput?.maleFemalePrice,
      bookingInput?.ticketId,
      bookingInput?.boothId,
      bookingInput?.bottleId,
      bookingInput?.alcoholic,
      bookingInput?.nonAlcoholic,
      bookingInput?.hasBoothBottleService,
      hasBoothBottleService,
      bookingIsBottleService,
      bookingIsClub,
      bookingIsTable,
    ]
  );

  useEffect(() => {
    fetchReviewSummary(appliedPromoRef.current);
  }, [fetchReviewSummary]);

  const handleApplyPromoCode = () => {
    const code = String(promoCodeInput || "").trim();
    setPromoError("");
    setAppliedPromoCode(code);
    fetchReviewSummary(code);
  };

  const handleRemovePromoCode = () => {
    setPromoCodeInput("");
    setAppliedPromoCode("");
    setPromoError("");
    fetchReviewSummary("");
  };

  const handlePromoInputChange = (value) => {
    setPromoCodeInput(value);
    if (!String(value || "").trim() && appliedPromoCode) {
      setAppliedPromoCode("");
      setPromoError("");
      fetchReviewSummary("");
    }
  };

  const handlePayNow = async () => {
    setPaymentError("");
    if (!validateGuestFields()) return;
    if (!validatePaymentFields()) return;
    try {
      setPaymentLoading(true);
      const cardElement = elements?.getElement(CardElement);
      if (!cardElement) {
        setPaymentError("Card details are not ready. Please try again.");
        return;
      }
      const pmResult = await stripe.createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name: String(cardName || "").trim(),
          email: String(userEmail || "").trim(),
        },
      });
      if (pmResult?.error || !pmResult?.paymentMethod?.id) {
        setPaymentError(pmResult?.error?.message || "Failed to create payment method.");
        return;
      }
      const formatDob = (value) => {
        const [y, m, d] = String(value || "").split("-");
        if (!y || !m || !d) return "";
        return `${d}-${m}-${y}`;
      };
      const toDayStartIso = (value) => {
        const d = String(value || "");
        return d ? `${d}T00:00:00.000Z` : "";
      };
      const toDayEndIso = (value) => {
        const d = String(value || "");
        return d ? `${d}T23:59:59.999Z` : "";
      };
      const alcoholicQuantity = (bookingInput.alcoholic || []).reduce(
        (sum, item) => sum + Number(item?.qunatity || item?.quantity || 0),
        0
      );
      const nonalcoholicQuantity = (bookingInput.nonAlcoholic || []).reduce(
        (sum, item) => sum + Number(item?.qunatity || item?.quantity || 0),
        0
      );
      const boothEventId = String(bookingInput.eventId || "").trim();
      const bottleServiceEventId = String(bookingInput.bottleId || "").trim();
      const boothOnlyBooking = isBoothTypeBooking && !hasBoothBottleService;
      const webBookingEventId =
        hasBoothBottleService && bottleServiceEventId ? bottleServiceEventId : boothEventId;
      const res = await postWebBooking({
        eventId: webBookingEventId,
        hostId: hostId || "",
        members: bookingInput.members || 1,
        promocode: String(appliedPromoCode || "").trim(),
        fees: Number(pricing.hst || 0),
        adminFee: Number(pricing.platformFee || 0),
        totalAmount: Number(pricing.total || 0),
        bookingStartDate: toDayStartIso(bookingInput.selectedDate),
        bookingEndDate: toDayEndIso(bookingInput.selectedEndDate || bookingInput.selectedDate),
        userName: String(userName || "").trim(),
        userEmail: String(userEmail || "").trim(),
        phoneNumber: normalizeUsPhoneForApi(contactNumber),
        contactNumber: normalizeUsPhoneForApi(contactNumber),
        dateOfBirth: formatDob(userDob),
        paymentMethodId: pmResult.paymentMethod.id,
        ...(usesTicketPricing && hasTicketBooking
          ? {
              ticketId: bookingInput.ticketId,
              ticketCost: Number(pricing.ticketPrice || 0),
              ticketType: String(
                bookingInput?.selectedTicket?.ticketType?.name ||
                  bookingInput?.selectedTicket?.ticketType?._id ||
                  bookingInput?.selectedTicket?.name ||
                  ""
              ).trim(),
            }
          : {}),
        ...(hasBoothBooking
          ? {
              boothId: bookingInput.boothId,
              boothCost: Number(pricing.ticketPrice || 0),
              boothType: pickBoothTypeId(bookingInput?.selectedBooth),
            }
          : {}),
        ...(hasBoothBottleService
          ? {
              boothId: boothEventId,
              boothCost: Number(pricing.boothCost || 0),
            }
          : {}),
        ...(boothOnlyBooking
          ? {
              boothId: boothEventId,
              boothCost: Number(pricing.boothCost || pricing.entryFeeAmount || 0),
            }
          : {}),
        bottleServiceCost: Number(pricing.bottleServiceTotal || 0),
        alcoholicQuantity: String(alcoholicQuantity),
        alcoholicPrice: String(Number(pricing.alcoholicTotal || 0)),
        nonalcoholicQuantity: String(nonalcoholicQuantity),
        nonalcoholicPrice: String(Number(pricing.nonAlcoholicTotal || 0)),
        alcoholicBottleServiceTotalPrice: Number(pricing.alcoholicTotal || 0),
        nonAlcoholicTotalPrice: Number(pricing.nonAlcoholicTotal || 0),
        discount: Number(pricing.discount || 0),
        gratuityAmount: Number(pricing.gratuityAmount || 0),
        type: isBottleServiceBooking ? "BottleService" : hasBoothBottleService || boothOnlyBooking ? "Booth" : undefined,
        alcoholic: isBottleServiceBooking || hasBoothBottleService
          ? (bookingInput.alcoholic || []).map((item) => ({
              BottleServiceId: item?.bottleServiceId || "",
              image: item?.image || "",
              name: item?.name || "",
              quantity: Number(item?.qunatity || item?.quantity || 0),
            }))
          : undefined,
        nonAlcoholic: isBottleServiceBooking || hasBoothBottleService
          ? (bookingInput.nonAlcoholic || []).map((item) => ({
              BottleServiceId: item?.bottleServiceId || "",
              image: item?.image || "",
              name: item?.name || "",
              quantity: Number(item?.qunatity || item?.quantity || 0),
            }))
          : undefined,
      });
      if (!isApiSuccess(res?.status)) {
        const apiMessage = res?.message || "Payment failed. Please try again.";
        if (/no such paymentmethod/i.test(apiMessage)) {
          setPaymentError(
            "Stripe keys do not match: this site created the card on one Stripe test account, but the server is using a different secret key. Use matching pk_test and sk_test keys from the same Stripe dashboard (Developers → API keys)."
          );
        } else {
          setPaymentError(apiMessage);
        }
        return;
      }
      setShowSuccessModal(true);
      try {
        sessionStorage.removeItem(guestFormStorageKey(eventId));
      } catch {
        /* ignore */
      }
    } catch (e) {
      setPaymentError(e?.message || "Payment failed. Please try again.");
    } finally {
      setPaymentLoading(false);
    }
  };

  if (!eventId) {
    return (
      <WebsiteLayout>
        <div className="container py-4" style={{ color: colors.red }}>
          Missing booking id.
        </div>
      </WebsiteLayout>
    );
  }

  if (!state?.bookingInput) {
    return (
      <WebsiteLayout>
        <div className="container py-4">
          <p style={{ color: colors.textColor }}>Review data not found.</p>
          <Link to={`/booking/${eventId}`} className="review-back-link">
            Back to booking
          </Link>
        </div>
      </WebsiteLayout>
    );
  }

  return (
    <WebsiteLayout>
      <div className="review-page-shell">
        <div className="container py-4 py-lg-5 px-3 px-md-4 review-page-inner">
          <BackNavButton
            to={fromEventUrl ? `/club/event/${encodeURIComponent(eventId)}` : ""}
            onClick={
              fromEventUrl
                ? undefined
                : () => {
                    if (window.history.length > 1) navigate(-1);
                    else navigate("/", { replace: true });
                  }
            }
            ariaLabel="Back"
          />

          <div className="review-card">
            <ReviewEventCard
              event={event}
              eventImage={eventImage}
              startDateLabel={startDateLabel}
              startTimeLabel={startTimeLabel}
              currentDayPrice={reviewCardPrice}
              hideDayPrice={reviewCardPrice == null}
            />

            <h3 className="review-section-title">Host Details</h3>
            <div className="review-row"><span>Full Name</span><strong>{event?.userId?.fullName || "-"}</strong></div>
            <div className="review-row"><span>Phone Number</span><strong>{event?.userId?.phone || "-"}</strong></div>
            <div className="review-row"><span>Email</span><strong>{event?.userId?.email || "-"}</strong></div>
            {(isBottleServiceBooking || hasBoothBottleService) && bottleDetails.length ? (
              <>
                <h3 className="review-section-title">Bottle Details</h3>
                {bottleDetails.map((item, idx) => (
                  <div className="review-row" key={`${item?.bottleServiceId || item?.name || "btl"}-${idx}`}>
                    <span>{`${item?.name || "Bottle"} (${item?.bottleType || "Bottle"})`}</span>
                    <strong>{getBottleLinePrice(item) || ""}</strong>
                  </div>
                ))}
              </>
            ) : null}

            <h3 className="review-section-title">Promo Code</h3>
            <div className="review-promo-row">
              <input
                className="review-promo-input"
                placeholder="Enter promo code"
                value={promoCodeInput}
                onChange={(e) => handlePromoInputChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (appliedPromoCode && !String(promoCodeInput || "").trim()) {
                      handleRemovePromoCode();
                    } else {
                      handleApplyPromoCode();
                    }
                  }
                }}
              />
              {appliedPromoCode ? (
                <button type="button" className="review-remove-btn" onClick={handleRemovePromoCode}>
                  Remove
                </button>
              ) : (
                <button type="button" className="review-apply-btn" onClick={handleApplyPromoCode}>
                  Apply
                </button>
              )}
            </div>
            {promoError ? <p style={{ color: colors.red, marginTop: 8 }}>{promoError}</p> : null}

            <PricingSummaryCard
              loading={loading}
              error={error}
              isTableBooking={isTableBooking}
              isBottleServiceBooking={isBottleServiceBooking || hasBoothBottleService}
              hasBoothBottleService={hasBoothBottleService}
              hasTicketBooking={hasTicketBooking}
              hasBoothBooking={hasBoothBooking}
              pricing={pricing}
              membersCount={membersCount}
              bookingDays={bookingDays}
            />

            <GuestUserForm
              userName={userName}
              setUserName={setUserName}
              userEmail={userEmail}
              setUserEmail={setUserEmail}
              userDob={userDob}
              setUserDob={setUserDob}
              contactNumber={contactNumber}
              setContactNumber={(value) => setContactNumber(formatUsPhoneMask(value))}
              nameError={nameError}
              setNameError={setNameError}
              emailError={emailError}
              setEmailError={setEmailError}
              dobError={dobError}
              setDobError={setDobError}
              contactError={contactError}
              setContactError={setContactError}
              minAdultDobIso={minAdultDobIso}
              isValidEmail={isValidEmail}
              isValidDob={isValidDob}
              dobInputRef={dobInputRef}
              openDobPicker={openDobPicker}
            />

            <div className="review-payment-wrap">
              <h4 className="review-user-title">Card Details</h4>
              <div className="review-user-field">
                <label htmlFor="reviewCardName">
                  Name on Card <span className="review-required-mark" aria-hidden="true">*</span>
                </label>
                <input
                  id="reviewCardName"
                  type="text"
                  aria-required="true"
                  autoComplete="cc-name"
                  value={cardName}
                  onChange={(e) => {
                    setCardName(e.target.value);
                    if (cardError) setCardError("");
                  }}
                  placeholder="Card holder name"
                />
              </div>
              <div className="review-user-field">
                <label htmlFor="reviewCardElement">
                  Card Information <span className="review-required-mark" aria-hidden="true">*</span>
                </label>
                <div className="review-card-element-wrap" id="reviewCardElement">
                  <CardElement
                    options={{
                      hidePostalCode: true,
                      disableLink: true,
                      style: {
                        base: {
                          color: "#ffffff",
                          fontSize: "16px",
                          "::placeholder": { color: "#a094bc" },
                        },
                        invalid: { color: "#ff7f95" },
                      },
                    }}
                    onChange={(e) => {
                      setCardComplete(Boolean(e?.complete));
                      if (e?.error?.message) setCardError(e.error.message);
                      else if (cardError) setCardError("");
                    }}
                  />
                </div>
              </div>
              {/* <div className="review-card-note">
                Use test cards in Stripe test mode.
              </div> */}
              {cardError ? <span className="review-field-error">{cardError}</span> : null}
              {paymentError ? <span className="review-field-error">{paymentError}</span> : null}
            </div>

            <button
              type="button"
              className="review-confirm-btn"
              onClick={handlePayNow}
              disabled={paymentLoading}
            >
              {paymentLoading ? "Processing..." : "Pay Now"}
            </button>
          </div>
        </div>
      </div>
      {showSuccessModal ? (
        <div className="review-success-modal">
          <div className="review-success-card">
            <h3>Congratulations!</h3>
            <p>Your payment was successful and booking is confirmed.</p>
             <p className="review-success-subtext">Download the app to unlock more features</p>
            <div className="review-store-links">
                
              <a href={APP_STORE_URL} target="_blank" rel="noreferrer">
                <FaApple />
                <span>App Store</span>
              </a>
              <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer">
                <FaGooglePlay />
                <span>Play Store</span>
              </a>
            </div>
         
            <Link
              className="review-confirm-btn review-ok-link"
              to="/"
              onClick={() => setShowSuccessModal(false)}
            >
              OK
            </Link>
          </div>
        </div>
      ) : null}
    </WebsiteLayout>
  );
}

export default function ReviewSummaryPage() {
  return (
    <Elements stripe={stripePromise}>
      <ReviewSummaryPageContent />
    </Elements>
  );
}
